/**
 * Player cards — E6 (`design/economy/economy-rebuild.md` §4.4), the second of the two
 * zero-wire-cost categories, and the simpler of the two: "None — meta layer only" per the
 * design table.
 *
 * A card is a frame, a background and a badge drawn behind a player's name in the scoreboard
 * and the career screen.
 *
 * ## It reaches the wire now — the "new decision" this file asked for was made (2026-09-17)
 *
 * This comment used to end "Do not add this to `MatchTicketClaims` or any realtime payload; if a
 * future design wants a card visible mid-match, that is a new decision, not an extension of this
 * one." Arturs made that decision on 2026-09-17, after equipping a card and finding that the
 * only place in the entire game it changed anything was one row on the Career screen. A cosmetic
 * nobody but its owner can ever see is a database column with a price on it.
 *
 * So a card now rides `MatchTicketClaims.card` (meta signs it, because a card is bought and a
 * client that named its own would be wearing one it never paid for) and `PLAYER_INFO.card` (one
 * byte, once per join, exactly the charm's shape). The per-tick budget the old note was
 * protecting is untouched: PLAYER_INFO is not snapshot traffic and `yarn budget` does not price
 * it — see that codec's own header for the full argument.
 *
 * Still true, and still the rule: NEVER a snapshot field and never a per-tick byte. A card does
 * not change during a match, so anything that pays for it more than once per join is wrong.
 *
 * Like a charm, equip is GLOBAL — one card per account, not per loadout slot: a card has
 * nothing to do with a weapon or a slot at all, it decorates the player's name.
 *
 * `0` is `NO_CARD` (Standard Issue, the free default every account starts with). Real cards are
 * `1..20`, stable once shipped.
 */

export const NO_CARD = 0;

/** Ids are stable and must never be reused — a `card:<id>` `UserItem` row persists them. */
export const PLAYER_CARD = {
  recruit: 1,
  veteran: 2,
  marksman: 3,
  vanguard: 4,
  sentinel: 5,
  ranger: 6,
  specter: 7,
  warden: 8,
  outrider: 9,
  reaper: 10,
  ironclad: 11,
  nightfall: 12,
  solstice: 13,
  wildfire: 14,
  tempest: 15,
  eclipse: 16,
  aurora: 17,
  obsidian: 18,
  celestial: 19,
  apex: 20,
} as const;

export type PlayerCardId = (typeof PLAYER_CARD)[keyof typeof PLAYER_CARD];

export interface PlayerCardDefinition {
  id: number;
  name: string;
  description: string;
  /** The frame drawn around the card edge and the name plate's border. sRGB hex. */
  frame: number;
  /** The fill behind the name — the darker stop against which `frame` and `badge` sit.
   * sRGB hex. */
  background: number;
  /** Icon key the client's icon atlas resolves for the badge in the card's corner. Pure
   * content — no geometry or texture is added per SKU, matching a charm's "one mesh, many
   * tints" economy but for a 2D icon sheet instead of a 3D socket. */
  badge: string;
  priceCoins: number;
  currency: 'PREMIUM';
}

export const PLAYER_CARDS: Readonly<Record<number, PlayerCardDefinition>> = {
  [PLAYER_CARD.recruit]: {
    id: PLAYER_CARD.recruit,
    name: 'Recruit',
    description: 'Plain issue. Everyone starts somewhere.',
    frame: 0x94a3b8,
    background: 0x1e293b,
    badge: 'chevron-1',
    priceCoins: 400,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.veteran]: {
    id: PLAYER_CARD.veteran,
    name: 'Veteran',
    description: 'Faded trim, plenty of matches behind it.',
    frame: 0xb45309,
    background: 0x27180a,
    badge: 'chevron-2',
    priceCoins: 500,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.marksman]: {
    id: PLAYER_CARD.marksman,
    name: 'Marksman',
    description: 'Green on olive. Range-day colours.',
    frame: 0x4d7c0f,
    background: 0x1a2410,
    badge: 'target',
    priceCoins: 550,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.vanguard]: {
    id: PLAYER_CARD.vanguard,
    name: 'Vanguard',
    description: 'Squared plates, front of the line.',
    frame: 0x64748b,
    background: 0x0f172a,
    badge: 'shield',
    priceCoins: 600,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.sentinel]: {
    id: PLAYER_CARD.sentinel,
    name: 'Sentinel',
    description: 'Ghillie green over dead leaf brown.',
    frame: 0x4ade80,
    background: 0x14261a,
    badge: 'eye',
    priceCoins: 650,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.ranger]: {
    id: PLAYER_CARD.ranger,
    name: 'Ranger',
    description: 'Forest colours, cut for distance work.',
    frame: 0x22c55e,
    background: 0x0f2417,
    badge: 'compass',
    priceCoins: 700,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.specter]: {
    id: PLAYER_CARD.specter,
    name: 'Specter',
    description: 'Grey on grey, barely there.',
    frame: 0xa1a1aa,
    background: 0x18181b,
    badge: 'ghost',
    priceCoins: 750,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.warden]: {
    id: PLAYER_CARD.warden,
    name: 'Warden',
    description: 'Steel blue, drawn square and heavy.',
    frame: 0x38bdf8,
    background: 0x0c1e2e,
    badge: 'lock',
    priceCoins: 800,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.outrider]: {
    id: PLAYER_CARD.outrider,
    name: 'Outrider',
    description: 'Dust orange on packed sand.',
    frame: 0xf97316,
    background: 0x2b1608,
    badge: 'wing',
    priceCoins: 850,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.reaper]: {
    id: PLAYER_CARD.reaper,
    name: 'Reaper',
    description: 'Black on black, one thin red line.',
    frame: 0xdc2626,
    background: 0x0a0a0a,
    badge: 'skull',
    priceCoins: 900,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.ironclad]: {
    id: PLAYER_CARD.ironclad,
    name: 'Ironclad',
    description: 'Riveted plate, cold and heavy.',
    frame: 0x71717a,
    background: 0x1c1c1f,
    badge: 'gear',
    priceCoins: 1000,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.nightfall]: {
    id: PLAYER_CARD.nightfall,
    name: 'Nightfall',
    description: 'Deep indigo, star-field badge.',
    frame: 0x6366f1,
    background: 0x0b0c1f,
    badge: 'star',
    priceCoins: 1100,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.solstice]: {
    id: PLAYER_CARD.solstice,
    name: 'Solstice',
    description: 'Warm gold over deep maroon.',
    frame: 0xfacc15,
    background: 0x2b0f0f,
    badge: 'sun',
    priceCoins: 1200,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.wildfire]: {
    id: PLAYER_CARD.wildfire,
    name: 'Wildfire',
    description: 'Orange over char black, still smoking.',
    frame: 0xea580c,
    background: 0x140a05,
    badge: 'flame',
    priceCoins: 1300,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.tempest]: {
    id: PLAYER_CARD.tempest,
    name: 'Tempest',
    description: 'Storm grey, a single lightning badge.',
    frame: 0xcbd5e1,
    background: 0x1e2a38,
    badge: 'bolt',
    priceCoins: 1400,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.eclipse]: {
    id: PLAYER_CARD.eclipse,
    name: 'Eclipse',
    description: 'A ring of gold around total black.',
    frame: 0xfbbf24,
    background: 0x000000,
    badge: 'ring',
    priceCoins: 1500,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.aurora]: {
    id: PLAYER_CARD.aurora,
    name: 'Aurora',
    description: 'Green-cyan wash, cold and bright.',
    frame: 0x2ee6d6,
    background: 0x082024,
    badge: 'wave',
    priceCoins: 1650,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.obsidian]: {
    id: PLAYER_CARD.obsidian,
    name: 'Obsidian',
    description: 'Glassy black, purple where the light catches it.',
    frame: 0x7b2ff7,
    background: 0x0a0512,
    badge: 'shard',
    priceCoins: 1800,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.celestial]: {
    id: PLAYER_CARD.celestial,
    name: 'Celestial',
    description: 'Deep space blue, a scatter of stars.',
    frame: 0x818cf8,
    background: 0x05061a,
    badge: 'constellation',
    priceCoins: 1900,
    currency: 'PREMIUM',
  },
  [PLAYER_CARD.apex]: {
    id: PLAYER_CARD.apex,
    name: 'Apex',
    description: 'Top of the rack. Nothing left to prove.',
    frame: 0xffffff,
    background: 0x111111,
    badge: 'crown',
    priceCoins: 2000,
    currency: 'PREMIUM',
  },
} as const;

export const PLAYER_CARD_LIST: readonly PlayerCardDefinition[] = Object.values(PLAYER_CARDS);
export const MAX_PLAYER_CARD_ID = Math.max(...PLAYER_CARD_LIST.map((card) => card.id));

export function isKnownPlayerCard(id: unknown): id is PlayerCardId {
  return typeof id === 'number' && Number.isInteger(id) && Object.prototype.hasOwnProperty.call(PLAYER_CARDS, id);
}

/** Item id under which ownership is recorded (`backend/src/game/sellback` sells it back through
 * this prefix without knowing player cards exist as a category). */
export function playerCardItemId(id: number): string {
  return `card:${id}`;
}
